<template>
  <!-- this should really be a view - will refactor -->
  <main class="tgbProducerDashboard">
    <h1 class="tgb-sr-only">Producer Dashboard</h1>

    <!-- should we abstract the layout of the directory as well? -->
    <div
      class="producer-dashboard-body"
      :class="{ detailsViewOpen: productionDetailsView }"
    >
      <div class="producer-dashboard-body__list">
        <div class="producerDashboardMainActions">
          <tgb-tabs
            :active-tab="activeTab"
            @tab-clicked="onClickedTab"
          >
            <template v-slot:tabAfterLast>
              <li class="tabs__trailing-tab">
                <tgb-button
                  variant="primary"
                  data-entity="producer-dashboard-createProduction"
                  @click="openCreateNewProductionModal()"
                >
                  <i class="far fa-plus"></i>
                  New Production
                </tgb-button>
              </li>
            </template>
          </tgb-tabs>
        </div>

        <!-- make ids match tab content ids for accessibility -->
        <TransitionGroup
          :name="tabAnimationClass"
          tag="div"
          class="producerDashboardContentTransition"
        >
          <div
            v-if="productionsView === 'all'"
            id="activeProductions"
            key="activeProductions"
            class="producerDashboardContent"
          >
            <section
              v-if="flaggedProductions.length > 0"
              class="producerDashboardSection"
            >
              <directory
                header="Starred"
                :activeItem="detailedProduction"
                :items="flaggedProductions"
                :page-size="flaggedIntPageSize"
                :selectedComponentView.sync="starredView"
                :show-subheader="currentlyShowingStarred > 0 && currentlyShowingStarred < flaggedProductions.length"
                :subheader="`Showing ${currentlyShowingStarred} of ${flaggedProductions.length}`"
                :totalCount="flaggedProductions.length"
                :totalVisibleCount.sync="currentlyShowingStarred"
                @dir-item-clicked="showProductionDetails"
                @dir-item-flagged="flagProduction"
                @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
                @on-dir-context-menu="onContextMenuActive"
              />
            </section>

            <section class="producerDashboardSection">
              <directory
                header="Productions"
                :activeItem="detailedProduction"
                :items="pagedProductions"
                :page-size="pagedProductions.length > pageSize ? pageSize : pagedProductions.length"
                :selectedComponentView.sync="allView"
                :show-subheader="currentlyShowingAll > 0 && currentlyShowingAll < totalActiveProductions"
                :subheader="`Showing ${currentlyShowingAll} of ${totalActiveProductions}`"
                :totalCount="totalActiveProductions"
                :totalVisibleCount.sync="currentlyShowingAll"
                :infinite="true"
                @dir-item-clicked="showProductionDetails"
                @dir-item-flagged="flagProduction"
                @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
                @load-more="loadMore"
                @on-dir-context-menu="onContextMenuActive"
              >
                <template v-slot:noDataMessage>
                  <div class="producerNoProductionsMessage">
                    <p class="tgbTitleLg">Hmm, you haven't created any cool productions.</p>
                    <p>Tell your story with Tagboard Producer!</p>
                  </div>
                </template>
              </directory>
            </section>
          </div>

          <div
            v-if="productionsView === 'deleted'"
            key="deletedProductions"
            class="producerDashboardContent"
            id="deletedProductions"
          >
            <div v-if="loading" class="loading loading--inline margin-md">
              <i class="icon icon-spinner"></i>
            </div>

            <section v-else class="producerDashboardSection">
              <directory
                header="Deleted"
                :subheader="`Showing ${currentlyShowingDeleted} of ${deletedProductions.length}`"
                :show-subheader="currentlyShowingDeleted > 0 && currentlyShowingDeleted < deletedProductions.length"
                :items="deletedProductions"
                :totalVisibleCount.sync="currentlyShowingDeleted"
                :selectedComponentView.sync="deletedView"
                @dir-item-clicked="showProductionDetails"
                :activeItem="detailedProduction"
                :totalCount="deletedProductions.length"
                :page-size="50"
                @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
                @on-dir-context-menu="onContextMenuDeleted"
              >
                <template v-slot:noDataMessage>
                  <div class="producerNoProductionsMessage">
                    <p class="tgbTitleLg">You haven't deleted any productions.</p>
                    <p>Remember to regularly clean out unused productions.</p>
                  </div>
                </template>
              </directory>
            </section>
          </div>
        </TransitionGroup>
      </div>

      <Transition name="slide-fade">
        <div class="producer-dashboard-body__details" v-if="productionDetailsView">
          <directory-details
            :key="detailedProduction"
            :details="productionDetailsView"
          >
            <template v-slot:beforeTitle>
              <button
                type="button"
                class="tb-btn tb-btn-text tgb-directory-menu-controls"
                data-entity="producer-details-closeDetails"
                @click="showNoDetails({ e: $event, payload: productionDetailsView})"
              >
                <span class="fas fa-times"></span>
              </button>

              <button
                type="button"
                class="tb-btn tb-btn-text tgb-directory-menu-controls"
                data-entity="producer-details-clickContextMenu"
                @click="detailsContextMenu({ e: $event, payload: productionDetailsView})"
              >
                <span class="fas fa-ellipsis-h"></span>
              </button>
            </template>

            <template v-slot:actions>
              <div class="tgbButtonRow tgbButtonReverse">
                <router-link
                  v-if="productionsView === 'all'"
                  class="tgbEnterProduction"
                  :to="{ name: 'themes', params: { production_id: productionDetailsView._id } }"
                  data-entity="producer-details-openProduction"
                  @click.native="sendSegmentEventLogs(productionDetailsView)"
                >
                  Enter Production
                </router-link>

                <tgb-button
                  v-else-if="productionsView === 'deleted'"
                  type="button"
                  variant="primary"
                  data-entity="producer-details-restoreProduction"
                  @click="onRestoreProduction(productionDetailsView, 'details_restore_production_button')"
                >
                  Restore Production
                </tgb-button>

                <tgb-button
                  variant="tertiary"
                  data-entity="producer-details-closeDetails"
                  @click.native="showNoDetails({ e: $event, payload: productionDetailsView})"
                >
                  Close Details
                </tgb-button>
              </div>
            </template>
          </directory-details>
        </div>
      </Transition>

      <tgb-context-menu
        v-if="showContextMenu"
        v-click-outside="hideContextMenu"
        :actions="currentContextMenu"
      >
        <template v-slot="action">
          <button
            class="tgbContextMenuButton"
            :data-entity="`producer-context-${action.key}`"
            @click="action.onClickFunc(action.item, action.trigger)"
          >
            <span
              v-if="action.icon"
              class="fas"
              :class="action.icon"
            />

            {{ action.text }}
          </button>
        </template>
      </tgb-context-menu>

      <form @submit.prevent="submitProduction" class="tgbModalForm">
        <tgb-modal
          ref="createNewProductionModal"
          @before-modal-close="pauseValidation"
          :headerText="(editProductionMode ? `Edit` : `New`) + ` Production`"
          @modal-close="clearNewProductionForm"
        >
          <tgb-select
            v-if="accounts"
            expanded
            name="accounts"
            placeholder="Select an account"
            label="Account"
            labelDescription="Select which account you'd like to create this production within."
            v-model="productionForm.owner"
            v-validate="'required'"
            :selectClass="`tgbSelectDropdown ${errors.has('accounts') ? 'critical' : ''}`"
          >
            <option
              v-if="canCreateProductions"
              v-bind:value="user.id">{{ user.displayName }} (Me)
            </option>

            <option
              :key="id"
              v-for="(account, id) in accounts.owners"
              v-bind:value="id"
            >
              {{ account.displayName }}
            </option>
          </tgb-select>

          <p v-if="errors.has('accounts')" class="formFieldError">
            <span class="fas fa-exclamation-triangle" />
            This is a required field.
          </p>

          <div class="tgbTextbox">
            <tgb-textbox
              expanded
              v-model="productionForm.title"
              label="Title"
              name="title"
              placeholder="Production Title"
              v-validate="'required|min:4|max:60'"
              :inputClass="`tgbInputTextbox ${errors.has('title') ? 'critical' : ''}`"
            />

            <p class="formFieldError"
               v-if="errors.has('title')"><span class="fas fa-exclamation-triangle"></span>
              Production title length must be between 4 and 60 characters.
            </p>
          </div>

          <label class="cursor-pointer">
            <input
              type="checkbox"
              class="tgb-sr-only"
              v-bind:value="productionForm.flagged"
              @click="productionFlagged"
            >

            <span
              class="fa-star"
              :class="productionForm.flagged ? 'fas' : 'far'"
            />

            <span class="tgbLabel">
              Add to Starred
            </span>
          </label>

          <tgb-tags
            v-model="productionForm.tags"
            @invalid="toggleInvalidTags"
          />

          <template v-slot:footer v-if="!editProductionMode">
            <!-- use css to make Save and Enter Production button be last visually because default
                 submit for form must be the first submit button in the dom when a form has multiple
                  submit buttons. This preserves the default form feature for a user to hit enter to
                  submit the form. -->
            <div class="tgbButtonRow tgbButtonReverse">
              <tgb-button
                id="tgbNewProductionSubmitAndEnter"
                type="submit"
                variant="primary"
                :disabled="isFormInvalid"
                data-entity="producer-dashboard-createAndOpenProduction"
              >
                Save and Enter Production
              </tgb-button>

              <tgb-button
                type="reset"
                variant="tertiary"
                data-entity="producer-dashboard-cancelCreateProduction"
                @click="closeCreateNewProductionModal"
              >
                Cancel
              </tgb-button>
            </div>
          </template>

          <template v-slot:footer v-else>
            <div class="tgbButtonRow tgbButtonReverse">
              <tgb-button
                type="submit"
                variant="primary"
                :disabled="isFormInvalid"
                data-entity="producer-dashboard-saveProductionDetails"
              >
                Save Changes
              </tgb-button>

              <tgb-button
                type="reset"
                variant="tertiary"
                data-entity="producer-dashboard-cancelEditProductionDetails"
                @click="closeCreateNewProductionModal"
              >
                Cancel
              </tgb-button>
            </div>
          </template>
        </tgb-modal>
      </form>
    </div>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Analytics from '../../utils/analytics';

import DirectoryDetails from '../../../components/ui/directory/DirectoryDetails.vue';
import Directory from '../../../components/ui/directory/Directory.vue';
import TgbTabs from '../../../components/ui/tabs/TgbTabs.vue';
import TgbModal from '../../../components/ui/modal/TgbModal.vue';
import TgbContextMenu from '../../../components/ui/context-menu/TgbContextMenu.vue';
import TgbTags from '../../../components/ui/tags/TgbTags.vue';
import TgbSelect from '../../../components/ui/select/TgbSelect.vue';
import TgbTextbox from '../../../components/ui/textbox/TgbTextbox.vue';
import TgbButton from '../../../components/ui/button/TgbButton.vue';

import GraphicsService from '../../services/graphics';

export default {
  name: 'ProducerDashboard',

  components: {
    DirectoryDetails,
    Directory,
    TgbTabs,
    TgbModal,
    TgbContextMenu,
    TgbTags,
    TgbSelect,
    TgbTextbox,
    TgbButton,
  },

  data() {
    return {
      allView: 'tgb-directory-table',
      starredView: 'tgb-directory-grid',
      deletedView: 'tgb-directory-table',
      searchProductionsString: '',
      productionsView: 'all',
      invalidTags: false,
      activeTab: 0,
      tabAnimationClass: 'productions-slide-right',
      productionDetailsView: null,
      currentlyShowingStarred: 0,
      currentlyShowingAll: 0,
      currentlyShowingDeleted: 0,
      contextMenuActive: [],
      contextMenuDeleted: [],
      editProductionMode: false,
      productionResetValues: {
        owner: null,
        title: '',
        tags: [],
        flagged: false,
        index: null,
        sub_index: null,
      },
      productionForm: {
        owner: null,
        title: '',
        tags: [],
        flagged: false,
        index: null,
        sub_index: null,
        timeline: [],
      },
      canCreateProductions: false,
      showContextMenu: false,
      detailedProduction: null,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      accounts: (state) => state.user.accounts,
      pagedProductions: (state) => state.production.pagedProductions,
      flaggedProductions: (state) => state.production.flaggedProductions,
      deletedProductions: (state) => state.production.deletedProductions,
      totalActiveProductions: (state) => state.production.totalActiveProductions,
      pageSize: (state) => state.production.limit,
      loading: (state) => state.production.loading,
    }),

    flaggedIntPageSize() {
      return this.flaggedProductions.length > 10 ? 10 : this.flaggedProductions.length;
    },

    detailsContextMenu() {
      return this.productionsView === 'deleted' ? this.onContextMenuDeleted : this.onContextMenuActive;
    },

    currentContextMenu() {
      return this.productionsView === 'deleted' ? this.contextMenuDeleted : this.contextMenuActive;
    },

    isFormInvalid() {
      if (this.invalidTags) {
        return true;
      }

      return Object.keys(this.fields)
        .some((key) => this.fields[key].invalid);
    },

    production() {
      return this.$store.state.production.production;
    },
  },

  methods: {
    ...mapActions([
      'incrementPage',
      'checkConnectedScreens',
      'getConnectedUsers',
      'getDeletedProductions',
      'getProductionsPaged',
      'getFlaggedProductions',
      'deleteProduction',
      'restoreProduction',
      'getUserAccounts',
    ]),

    toggleInvalidTags(v) {
      this.invalidTags = v;
    },

    productionFlagged() {
      this.productionForm.flagged = !this.productionForm.flagged;
    },

    sendSegmentEventLogs(production) {
      Analytics.log({
        event: 'Producer Launched Production',
        fields: {
          production_id: production._id,
          title: production.title,
          tags: production.tags,
          starred: production.flagged,
          trigger: 'details_panel',
        },
        ownerId: production.owner,
      });
    },

    async createNewProduction() {
      this.$store.dispatch('startLoader');

      let data;
      try {
        data = await this.$store.dispatch('createProduction', this.productionForm);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error creating production:', err);

        this.errorToast(
          'There was an error creating your production. Please try again.',
        );

        this.$store.dispatch('stopLoader');

        return;
      }

      this.successToast('Production created successfully');

      Analytics.log({
        event: 'Producer Created Production',
        fields: {
          production_id: data._id,
          tags: data.tags,
          starred: data.flagged,
          title: data.title,
        },
        ownerId: data.owner,
      });

      this.$router.push({
        name: 'themes',
        params: { production_id: data._id },
      });

      this.$store.dispatch('stopLoader');
    },

    async updateProduction() {
      this.$store.dispatch('startLoader');

      const productionFormData = this.productionForm;

      const {
        _id: productionId,
        flagged,
      } = productionFormData;

      delete productionFormData.flagged;
      const currentProduction = this.$store.state.production.pagedProductions.find((p) => p._id === productionId); // eslint-disable-line max-len

      // Only handling flagging if the value has changed.
      // This improves response time and lowers api calls.
      if (flagged !== currentProduction.flagged) {
        await this.flagProduction(currentProduction);
      }

      try {
        await this.$store.dispatch('updateProduction', productionFormData);
        this.successToast('Production saved!');
        this.closeCreateNewProductionModal();
      } catch (err) {
        this.errorToast('There was an error creating your production. Please try again.');
      }

      this.$store.dispatch('stopLoader');
    },

    submitProduction() {
      if (this.editProductionMode) {
        this.updateProduction();
      } else {
        this.createNewProduction();
      }
    },

    pauseValidation() {
      // prevents console errors from validation trying to validate
      // elements removed from the dom
      this.$validator.pause();
    },

    openCreateNewProductionModal() {
      this.$refs.createNewProductionModal.open();
      this.editProductionMode = false;
      this.$validator.resume();
    },

    closeCreateNewProductionModal() {
      this.$refs.createNewProductionModal.close();
    },

    openEditProductionsModal(production, trigger) {
      this.editProductionMode = true;
      this.$refs.createNewProductionModal.open();
      this.$validator.resume();

      const {
        owner,
        title,
        tags,
        flagged,
        _id,
      } = { ...production };

      this.productionForm = {
        owner,
        title,
        tags,
        flagged,
        _id,
        trigger,
      };
    },

    animationDirectionSwitch(current, next) {
      if (current > next) {
        this.tabAnimationClass = 'productions-slide-left';
      } else {
        this.tabAnimationClass = 'productions-slide-right';
      }
    },

    onClickedTab({ tab, index }) {
      this.animationDirectionSwitch(this.activeTab, index);
      this.productionsView = tab.contentId;
      this.activeTab = index;

      this.showNoDetails({
        payload: {
          ...this.productionDetailsView,
          trigger: 'productions_list_tabs',
        },
      });

      this.$router.push({
        name: 'productions',
        params: { tab: tab.contentId },
      });

      // Only load deleted productions if user navigated to that tab
      if (tab.contentId === 'deleted' && !this.$store.state.production.loadedDeletedProductions) {
        this.loadDeletedProductions();
      }
    },

    loadDeletedProductions() {
      this.getDeletedProductions()
        .then((data) => {
          const view = localStorage.productionsDeletedView || this.deletedView;
          Analytics.log({
            event: 'Producer Loaded Deleted Productions List',
            fields: {
              deletedProductionsCount: data.length,
              view: view === 'tgb-directory-grid' ? 'card' : 'table',
            },
          });
        });
    },

    flagProduction(production) {
      return this.$store.dispatch('flagUpdateInProductions', production)
        .catch(() => {
          this.errorToast(
            'There was an error flagging your production.',
          );
        });
    },

    showProductionDetails(production) {
      this.productionDetailsView = production;
      this.detailedProduction = production._id;
    },

    showNoDetails({
      e,
      payload,
    }) {
      Analytics.log({
        event: 'Producer Details Panel Closed',
        fields: {
          production_id: payload._id,
          tags: payload.tags,
          starred: payload.flagged,
          trigger: payload.trigger || (e ? e.currentTarget.dataset.tracking : null),
        },
        ownerId: payload.owner,
      });

      this.productionDetailsView = null;
      this.detailedProduction = null;
    },

    async loadMore() {
      this.incrementPage();

      await this.getProductionsPaged();

      // Fetch screens + connected users for the new productions we load
      this.checkConnectedScreens();
      this.getConnectedUsers();
    },

    onDeleteProduction(item, trigger) {
      this.showContextMenu = false;

      this.deleteProduction({
        production: item,
        trigger,
      });

      this.showNoDetails({
        payload: {
          ...item,
          trigger,
        },
      });
    },

    onRestoreProduction(item, trigger) {
      this.restoreProduction({
        production: item,
        trigger,
      });

      this.showNoDetails({
        payload: {
          ...item,
          trigger,
        },
      });
    },

    async onDuplicateProduction(item) {
      this.$store.dispatch('startLoader');

      this.productionForm.title = `${item.title} (Copy)`;
      this.productionForm.owner = item.owner;
      this.productionForm.tags = item.tags;
      this.productionForm.flagged = item.flagged;
      this.showContextMenu = false;

      try {
        const { timeline } = await this.$store.dispatch('getProductionV2', item._id);
        this.productionForm.timeline = timeline;

        const newProduction = await this.$store.dispatch('createProduction', this.productionForm);

        const owner = { owner: item.owner };
        await GraphicsService.duplicateSavedGraphics(item._id, newProduction._id, owner);

        this.successToast('Production duplicated successfully');
      } catch (err) {
        this.errorToast(
          'There was an error duplicating the production. Please try again.',
        );
      } finally {
        this.$store.dispatch('stopLoader');
      }
    },

    hideContextMenu() {
      this.showContextMenu = false;
    },

    onContextMenuActive({
      e,
      payload,
    }) {
      this.contextMenuActive = [
        {
          key: 'openProduction',
          text: 'Open',
          item: payload,
          icon: 'fa-sign-in-alt',
          onClickFunc: this.enterProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
        {
          key: 'editDetails',
          text: 'Edit Details',
          item: payload,
          icon: 'fa-edit',
          onClickFunc: this.openEditProductionsModal,
          trigger: e.currentTarget.dataset.tracking,
        },
        {
          key: 'duplicateProduction',
          text: 'Duplicate',
          item: payload,
          icon: 'fa-copy',
          onClickFunc: this.onDuplicateProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
        {
          key: 'deleteProduction',
          text: 'Delete',
          item: payload,
          icon: 'fa-trash',
          onClickFunc: this.onDeleteProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
      ];

      this.showContextMenu = true;
      Analytics.log({
        event: 'Producer Triggered Context Menu Production',
        fields: {
          production_id: payload._id,
          title: payload.title,
          tags: payload.tags,
          starred: payload.flagged,
          trigger: e.currentTarget.dataset.tracking,
        },
        ownerId: payload.owner,
      });
    },

    onContextMenuDeleted({ e, payload }) {
      this.contextMenuDeleted = [
        {
          key: 'restoreProduction',
          text: 'Restore',
          item: payload,
          icon: 'fa-reply-all',
          onClickFunc: this.onRestoreProduction,
          trigger: e.currentTarget.dataset.tracking,
        },
      ];

      this.showContextMenu = true;
    },

    onDropDownMenuItemClicked(payload) {
      const {
        callbackName,
        item,
        trigger,
      } = payload;

      this[callbackName](item, trigger);
    },

    enterProduction(production) {
      this.$router.push({
        name: 'themes',
        params: { production_id: production._id },
      });

      Analytics.log({
        event: 'Producer Launched Production',
        fields: {
          production_id: production._id,
          title: production.title,
          tags: production.tags,
          starred: production.flagged,
          trigger: 'context_menu',
        },
        ownerId: production.owner,
      });
    },

    clearNewProductionForm() {
      this.productionForm = { ...this.productionResetValues };
    },

    setCancelEditProductionValue(production) {
      this.cancelProductionValues = production;
    },
  },

  watch: {
    allView(view) {
      localStorage.productionsAllView = view;
    },

    starredView(view) {
      localStorage.productionsStarredView = view;
    },

    deletedView(view) {
      localStorage.productionsDeletedView = view;
    },
  },

  mounted() {
    if (localStorage.productionsAllView) {
      this.allView = localStorage.productionsAllView;
    }

    if (localStorage.productionsStarredView) {
      this.starredView = localStorage.productionsStarredView;
    }

    if (localStorage.productionsDeletedView) {
      this.deletedView = localStorage.productionsDeletedView;
    }
  },

  created() {
    this.getProductionsPaged()
      .then((data) => {
        this.checkConnectedScreens();
        this.getConnectedUsers();

        const view = localStorage.productionsAllView ? localStorage.productionsAllView : this.allView;
        Analytics.log({
          event: 'Producer Loaded Productions List',
          fields: {
            allProductionsCount: data.totalCount,
            view: view === 'tgb-directory-grid' ? 'card' : 'table',
          },
        });
      });

    this.getFlaggedProductions()
      .then((productions) => {
        const view = localStorage.productionsStarredView ? localStorage.productionsStarredView : this.starredView;

        // Developer NOTE: Seems redundant to have this AND the above event when they both fire together
        Analytics.log({
          event: 'Producer Loaded Flagged Productions List',
          fields: {
            starredProductionsCount: productions.length,
            view: view === 'tgb-directory-grid' ? 'card' : 'table',
          },
        });
      });

    this.getUserAccounts()
      .then(async (data) => {
        const { owners } = data;

        const userDataFirstOwner = parseInt(Object.keys(owners)[0], 10);
        const userData = await this.$store.dispatch('getUserPermissions');
        this.canCreateProductions = userData.canCreateProductions;
        const owner = userData.canCreateProductions ? this.user.id : userDataFirstOwner;
        this.productionResetValues.owner = owner;
        this.productionForm.owner = owner;
      });

    if (this.$route.params.tab === 'deleted') {
      this.productionsView = 'deleted';
      this.activeTab = 1;
      this.loadDeletedProductions();
    }
  },
};
</script>

<style lang="scss">
//reset some bootstrap stuff just here. Delete when we remove the BS dependency
label {
  display: auto;
  max-width: auto;
  margin-bottom: auto;
  font-weight: inherit;
}

// styles that belong here
.producer-dashboard-body {
  &.detailsViewOpen {
    display: grid;
    grid-template-columns: auto 444px;
    grid-auto-rows: 100%;
  }

  height: calc(100vh - 60px);
}

.producer-dashboard-body__list {
  padding: 3.5rem 2.5rem;
  height: 100%;
  overflow: auto;
}

.producer-dashboard-body__details {
  height: 100%;
  width: 444px;
  overflow: auto;
  border-left: 1px solid #252525;
}

.producerDashboardSection {
  &:not(:last-child) {
    margin-bottom: 72px; // we need to talk about grid and spacing increments
  }
}

.producerDetailsVersion {
  flex-grow: 1;
}

.producerDashboardContentTransition {
  position: relative;
  overflow: visible;
}

.productions-slide-left-leave-active,
.productions-slide-left-enter-active,
.productions-slide-right-leave-active,
.productions-slide-right-enter-active {
  transition: 400ms;
  position: absolute;
  top: 0;
  width: 100%;
}

.productions-slide-right-leave-active {
  right: 36px;
}

.productions-slide-left-leave-active {
  left: 36px;
}

.productions-slide-right-enter,
.productions-slide-left-leave-to {
  transform: translate(100%, 0);
}

.productions-slide-right-leave-to,
.productions-slide-left-enter {
  transform: translate(-100%, 0);
}

// styles that eventually should be moved to a more specific place
.tgb-form-group {
  display: flex;

  .icon {
    position: absolute;
    width: 35px;
    height: 35px;
    text-align: center;
    font-size: 15px;
    line-height: 30px;
  }

  .tgb-input-icon {
    padding-left: 35px;
  }
}

.tgbField {
  margin-top: 40px;
}

.tgbLabel {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
}

.tgbTitleLg {
  font-style: normal;
  font-weight: 900;
  font-size: 18px;
  line-height: 100%;
  word-break: break-all;
}

.tgb-input {
  display: block;
  width: 100%;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  color: #e1e1e1;
  background-color: var(--tgb-surface-default-color);
  border-radius: var(--tgb-border-radius-default);
  border: 1px solid transparent;
  outline: 0;
  box-shadow: none;
}

.tgbMetaLabel {
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #808689; //text primary inactive (not sure I like using inactive)
}

.tgbDirectoryBeforeTitle {
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
}

.tbgIconCheck::before, .tgb-icon-ellipsis::before {
  font-size: 16px;
  cursor: pointer;
}

.tbgIconChecked {
  color: #23CDF4; //todo: set to var based on primary
}

// todo: when we make a context menu items default component this should go there
.tgbContextMenuButton {
  .fas {
    margin-right: 8px;
  }
}

.tgbDirectoryMenuControls {
  font-size: 18px;
}

.tgb-btn-group {
  display: flex;
  gap: 12px; // there is a better place for this when we start making a global btn group
  align-items: center;
  justify-content: center;
}

//should be global
//utility classes should be global and more generated
.tgb-flex {
  display: flex;
}

.tgbNoWrap {
  white-space: nowrap;
}

.tgb-text-bold {
  font-weight: 700;
}

[aria-role="button"] {
  cursor: pointer;
}

.tgb-full-width {
  width: 100%
}

.modalContentTitle {
  color: #e1e1e1;
}

.tgbModalForm {
  .tgbModal {
    max-width: 700px;
  }
}

.formFieldError {
  font-family: Barlow, sans-serif;;
  font-weight: 700;
  font-size: 14px;
  color: #FF9E98;
  margin-top: 12px;
}

.tgbSelectDropdown {
  width: 615px;
  padding: 12px;
  border: 1px solid #141B20;
  margin-top: 10px;
  //background-color: #0F1B1E !important; // this is for the new theme
  background-color: #333; // to match old theme
  background-image: none;

  &:focus {
    border-color: #23CDF4;
    box-shadow: 0px 0px 0px 4px rgba(43, 172, 202, 0.16);
    border-radius: 2px;
  }

  &.critical,
  &.critical:focus {
    border-color: #FF766D;
    box-shadow: 0px 0px 0px 4px rgba(255, 118, 109, 0.24);
    border-radius: 2px;
  }
}

.tgbTextbox {
  margin-top: 40px;
  margin-bottom: 40px;
}

.tgbInputTextbox,
.tgbTagsInputContainer {
  //background-color: #0F1B1E; // this is for the new theme
  background-color: #333; // to match old theme
  padding: 12px;
  border: 1px solid #141B20;
  margin-top: 10px;

  &:focus,
  &:focus-within {
    border-color: #23CDF4;
    box-shadow: 0px 0px 0px 4px rgba(43, 172, 202, 0.16);
    border-radius: 2px;
  }

  &.critical,
  &.critical:focus,
  &.critical:focus-within {
    border-color: #FF766D;
    box-shadow: 0px 0px 0px 4px rgba(255, 118, 109, 0.24);
    border-radius: 2px;
  }
}

//todo: tags should be a component
.tgbTags {
  display: flex;
  grid-gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

// this is hacky, but it's a workaround for lack of component features in oruga
.tgbTagsInputContainer {
  & > div {
    @extend .tgbTags;
  }
}

.tgbTag {
  padding: 8px 12px;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  border-radius: 4px; // todo: check this radius as the other global things are 5px
  background: #253c42; // todo: this color is mapped to no theme color in the mockups
  color: #23CDF4; // todo: this color is mapped to no theme color in the mockups

  &::before {
    content: "#";
  }
}

.tgbButtonRow {
  display: flex;
  gap: 8px;

  &.tgbButtonReverse {
    flex-direction: row-reverse;
    margin-left: auto;
  }
}

.tgbModalFooter {
  margin-top: 40px;
}

.tgbEnterProduction {
  padding: 10px 15px;
  font-weight: 700;
  font-family: Barlow, sans-serif;
  font-style: normal;
  font-size: 14px;
  line-height: 14px;
  gap: 8px;
  cursor: pointer;
  color: #fff !important;
  border: #007F9D;
  border-radius: 2px;
  background-color: #007F9D;
  margin-left: 10px;

  &:hover {
    background: #109BB8;
    border-color: #109BB8;
  }

  &:active {
    background: #2BACCA;
    border-color: #2BACCA;
  }

  &:disabled {
    color: #333333;
    border-color: #6D7D85;
    background-color: #6D7D85;
  }
}

.producerNoProductionsMessage {
  padding-top: 100px;
  text-align: center;
}
</style>
